// src/hooks/useInvoices.js - Hook replacement that uses InvoiceStore
import { useMemo } from 'react';
import useInvoiceStore from '../stores/InvoiceStore';
import { useSocket } from '../contexts/SocketContext';

/**
 * Custom hook for accessing invoice data from InvoiceStore
 * 
 * UPDATED: Now uses Zustand InvoiceStore instead of InvoicesProvider
 * Store initialization is handled at app level in SocketContext.
 * 
 * FIXED: Uses proper Zustand selectors instead of destructuring entire store
 * 
 * @returns {object} Invoice data and operations
 */
export const useInvoices = () => {
  const socket = useSocket();
  
  // 🚨 FIXED: Use individual selectors instead of destructuring entire store
  
  // Main invoices data
  const invoices = useInvoiceStore(state => state.invoices);
  const invoicesLoading = useInvoiceStore(state => state.invoicesLoading);
  const invoicesError = useInvoiceStore(state => state.invoicesError);
  const invoicesPagination = useInvoiceStore(state => state.invoicesPagination);
  
  // Selected invoice data
  const selectedInvoice = useInvoiceStore(state => state.selectedInvoice);
  const selectedInvoiceLoading = useInvoiceStore(state => state.selectedInvoiceLoading);
  const selectedInvoiceError = useInvoiceStore(state => state.selectedInvoiceError);
  
  // Financial stats
  const invoiceStats = useInvoiceStore(state => state.invoiceStats);
  
  // Invoice operations (functions don't change, so we can get them once)
  const loadInvoices = useInvoiceStore(state => state.loadInvoices);
  const loadInvoiceDetails = useInvoiceStore(state => state.loadInvoiceDetails);
  const loadInvoiceSummary = useInvoiceStore(state => state.loadInvoiceSummary);
  const createInvoice = useInvoiceStore(state => state.createInvoice);
  const updateInvoice = useInvoiceStore(state => state.updateInvoice);
  const deleteInvoice = useInvoiceStore(state => state.deleteInvoice);
  const sendInvoice = useInvoiceStore(state => state.sendInvoice);
  const createAndSendInvoice = useInvoiceStore(state => state.createAndSendInvoice);
  const recordPayment = useInvoiceStore(state => state.recordPayment);
  const updatePaymentStatus = useInvoiceStore(state => state.updatePaymentStatus);
  
  // Line item operations
  const addLineItem = useInvoiceStore(state => state.addLineItem);
  const updateLineItem = useInvoiceStore(state => state.updateLineItem);
  const removeLineItem = useInvoiceStore(state => state.removeLineItem);
  const reorderLineItems = useInvoiceStore(state => state.reorderLineItems);
  
  // Utility functions
  const clearInvoicesError = useInvoiceStore(state => state.clearInvoicesError);
  const clearSelectedInvoice = useInvoiceStore(state => state.clearSelectedInvoice);
  const refreshInvoices = useInvoiceStore(state => state.refreshInvoices);
  const getInvoiceById = useInvoiceStore(state => state.getInvoiceById);
  const getCustomerInvoices = useInvoiceStore(state => state.getCustomerInvoices);
  const calculateTotals = useInvoiceStore(state => state.calculateTotals);
  const getOverdueInvoices = useInvoiceStore(state => state.getOverdueInvoices);
  const getRecentInvoices = useInvoiceStore(state => state.getRecentInvoices);
  
  // Connection state
  const isConnected = useInvoiceStore(state => state.isConnected);
  
  // Internal initialization state (for fallback check)
  const _initialized = useInvoiceStore(state => state._initialized);
  
  // Fallback initialization check (safety net)
  // If app-level initialization failed but we're authenticated, try fallback
  if (socket.isAuthenticated && !socket.storesInitialized && !_initialized) {
    console.warn('⚠️ useInvoices: Store not initialized at app level, trying fallback');
    socket.initializeStore('InvoiceStore');
  }
  
  // Computed values (from InvoicesProvider)
  const overdueInvoices = useMemo(() => {
    return getOverdueInvoices();
  }, [getOverdueInvoices]);
  
  const recentInvoices = useMemo(() => {
    return getRecentInvoices();
  }, [getRecentInvoices]);
  
  // Return the exact same interface as the old InvoicesProvider
  return {
    // Main invoices data (single source of truth)
    invoices,
    invoicesLoading,
    invoicesError,
    invoicesPagination,
    
    // Selected invoice data
    selectedInvoice,
    selectedInvoiceLoading,
    selectedInvoiceError,
    
    // Financial stats
    invoiceStats,
    
    // Computed values
    overdueInvoices,
    recentInvoices,
    
    // Invoice operations
    loadInvoices,
    loadInvoiceDetails,
    loadInvoiceSummary,
    createInvoice,
    updateInvoice,
    deleteInvoice,
    sendInvoice,
    createAndSendInvoice,
    recordPayment,
    updatePaymentStatus,
    
    // Line item operations
    addLineItem,
    updateLineItem,
    removeLineItem,
    reorderLineItems,
    
    // Utility functions
    clearInvoicesError,
    clearSelectedInvoice,
    refreshInvoices,
    getInvoiceById,
    getCustomerInvoices,
    calculateTotals,
    
    // Connection state
    isConnected: socket.isAuthenticated,
    
    // Legacy aliases for backward compatibility
    loading: invoicesLoading,
    error: invoicesError,
    pagination: invoicesPagination,
    selectedLoading: selectedInvoiceLoading,
    selectedError: selectedInvoiceError,
    clearError: clearInvoicesError,
    
    // Additional legacy aliases that might exist
    stats: invoiceStats,
    overdue: overdueInvoices,
    recent: recentInvoices
  };
};

export default useInvoices;